import cx from 'classnames'
import { useState, useRef, useEffect } from 'react'

import Styles from './PurchaseContentCreditsModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { StripePrice } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { formatPrice } from 'components/Settings/TieredBilling/utils'
import { StripePaymentForm, StripePaymentFormRef } from 'components/StripePaymentForm'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useSetupIntent } from 'components/TimeTriggerPaywall/screens/Checkout/Checkout'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { outstandingInvoiceMessage } from 'utils/subscriptionErrorMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AddMoreContentModal } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/AddMoreContentModal/AddMoreContentModal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useRootStore } from 'stores/RootStore'
import { useUpdateAutoReloadCredits } from 'hooks/useUpdateAutoReloadCredits'
import { EnableAutoReload } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/components/EnableAutoReload/EnableAutoReload'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { SparkleFilled } from 'almanac-ui/icons/16'
import { ArrowRefresh2, ChevronDown2 } from 'blaze-ui/icons'
import { AutoReloadModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/AutoReloadModal/AutoReloadModal'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { Check02 } from 'blaze-ui/icons/16'

export const PurchaseContentCreditsModal = ({
  price,
  defaultBatchCount = 1,
  onSuccess,
  closeOnSuccess = true,
}: {
  price: StripePrice
  onSuccess?: () => void
  defaultBatchCount?: number
  closeOnSuccess?: boolean
}) => {
  const [shouldAutoReloadCredits, setAutoReloadCredits] = useState(true)
  const [batches, setBatches] = useState(defaultBatchCount)
  const [isProcessing, setIsProcessing] = useState(false)
  const [hasPaymentErrors, setHasPaymentErrors] = useState(true)
  const { closeModal, openModal } = useModals()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspace = useCurrentWorkspace()
  const { data: clientSecret, isLoading } = useSetupIntent(currentWorkspaceId)
  const stripeFormRef = useRef<StripePaymentFormRef>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const { creditUsageStore, tierUsageStore } = useRootStore()
  const hasExistingCard = tierUsageStore.currentSubscription?.card?.last_four
  const { creditsRemaining, canAutoReload } = useCreditUsage()
  const showAutoReloadCreditsToggle = workspace && !workspace.auto_reload_credits
  const autoReloadIsPaused = workspace?.auto_reload_credits && !canAutoReload
  const updateAutoReloadCredits = useUpdateAutoReloadCredits()

  useEffect(() => {
    if (creditsRemaining <= 0) {
      setBatches(Math.floor(Math.abs(creditsRemaining) / 100) + 1)
    }
  }, [creditsRemaining])

  const handlePaymentMethodCreated = async (paymentMethodId?: string) => {
    if (!currentWorkspaceId) return

    setIsProcessing(true)

    const res = await WorkspaceClient.purchaseContentCredits(currentWorkspaceId, batches, price.id, paymentMethodId)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      if (showAutoReloadCreditsToggle) {
        const autoReloadLimit = shouldAutoReloadCredits ? 500 : null
        updateAutoReloadCredits.mutate({ autoReloadCredits: shouldAutoReloadCredits, autoReloadLimit })
      }
      showToastMessage('Success! Add more content now.', 'success', {
        type: 'button',
        variant: 'proceed',
        label: 'Create Content',
        onPress: () => {
          openModal(AddMoreContentModal, { allowWizardsModal: true })
        },
      })
      creditUsageStore.refetch(currentWorkspaceId)
      onSuccess?.()
      if (closeOnSuccess) {
        closeModal()
      }
      setIsProcessing(false)
      return
    }

    setIsProcessing(false)
    // Only override the generic toast when the failure is determinably the
    // outstanding-invoice guard.
    const invoiceMessage = outstandingInvoiceMessage(res)
    showToastMessage(invoiceMessage ?? 'Failed to purchase credits', 'error')
  }

  const addCredits = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (showConfirmation) {
      if (stripeFormRef.current) {
        await stripeFormRef.current.submitPayment()
      }
      return
    }

    const hasFormErrors = stripeFormRef.current?.hasErrors() ?? false
    if (hasFormErrors && hasPaymentErrors) {
      if (stripeFormRef.current) {
        await stripeFormRef.current.submitPayment()
      }
      return
    }

    setShowConfirmation(true)
  }

  const toggleAutoReloadCredits = () => {
    setAutoReloadCredits(!shouldAutoReloadCredits)
  }

  const openAutoReloadModal = () => {
    openModal(AutoReloadModal)
  }

  return (
    <Modal.Root onPressOutside={closeModal}>
      <Modal.Header title={showConfirmation ? 'Confirm purchase' : 'Purchase credits to continue'} />
      <form onSubmit={addCredits}>
        <Modal.Content>
          <div className={Styles.content}>
            {showConfirmation ? (
              <div className={Styles.confirmationContainer}>
                <Text>You'll be immediately charged ${formatPrice((price.unit_amount * batches) / 100)}.</Text>
                <div className={Styles.confirmationTotal}>
                  <Text variant="largeList">Total</Text>
                  <Heading level={4} color="var(--dark-80)">
                    ${formatPrice((price.unit_amount * batches) / 100)}
                  </Heading>
                </div>
              </div>
            ) : null}

            {autoReloadIsPaused && !showConfirmation && (
              <div className={Styles.autoReloadPausedContainer}>
                <div className={Styles.autoReloadPausedState}>
                  <ArrowRefresh2 size={20} />
                  <Text variant="secondary">Auto-reload</Text>
                  <div className={Styles.autoReloadPausedPill}>PAUSED</div>
                  <Text variant="secondary" color="var(--dark-40)">
                    $50 budget reached
                  </Text>
                </div>
                <Button variant="secondary" size="sm" onPress={openAutoReloadModal}>
                  Manage
                </Button>
              </div>
            )}
            {showAutoReloadCreditsToggle && (
              <div className={cx({ [Styles.hidden]: showConfirmation })}>
                <EnableAutoReload
                  shouldAutoReloadCredits={shouldAutoReloadCredits}
                  toggleAutoReloadCredits={toggleAutoReloadCredits}
                  name="enable-auto-reload-credits"
                />
              </div>
            )}
            {!showConfirmation && (
              <div className={Styles.remainingCreditsRow}>
                <Text variant="largeList">Remaining credits</Text>
                <Text>{creditsRemaining.toLocaleString()} Credits</Text>
              </div>
            )}
            {!showConfirmation && (
              <div className={Styles.creditsSection}>
                <div className={Styles.creditsInput}>
                  <Text variant="largeList">Add-on credits</Text>
                </div>
                <DropdownTrigger placement="bottom-end">
                  <Button variant="secondary" endIcon={ChevronDown2} size="lg">
                    <span className={Styles.buttonText}>
                      {batches * 100} credits: ${batches * 10}
                    </span>
                  </Button>
                  {(close) => (
                    <Menu
                      className={Styles.dropdownMenu}
                      onAction={(key) => {
                        setBatches(parseInt(key as string))
                        close()
                      }}
                    >
                      {[1, 2, 4, 8, 12, 20].map((batch) => (
                        <Item key={batch}>
                          <div className={Styles.dropdownItem}>
                            <Text variant="secondary">
                              {batch * 100} credits: ${batch * 10}
                            </Text>
                            {batches === batch && (
                              <div className={Styles.checkIconContainer}>
                                <Check02 size={10} color="var(--light-100)" />
                              </div>
                            )}
                          </div>
                        </Item>
                      ))}
                    </Menu>
                  )}
                </DropdownTrigger>
              </div>
            )}

            <div className={cx(Styles.paymentSection, { [Styles.hidden]: showConfirmation })}>
              <StripePaymentForm
                ref={stripeFormRef}
                clientSecret={clientSecret}
                onPaymentMethodCreated={handlePaymentMethodCreated}
                onErrorChange={setHasPaymentErrors}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
              />
            </div>
            {creditsRemaining < 0 && !showConfirmation && (
              <div className={Styles.negativeBalanceNotice}>
                <Text variant="secondary" color="var(--red-50)">
                  Your current balance is {creditsRemaining} credits. Purchase at least{' '}
                  {(Math.floor(Math.abs(creditsRemaining) / 100) + 1) * 100} credits to restore a positive balance.
                </Text>
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button variant="tertiary" onPress={() => (showConfirmation ? setShowConfirmation(false) : closeModal())}>
              {showConfirmation ? 'Back' : 'Cancel'}
            </Button>
          </Modal.FooterContent>
          <Modal.FooterButton type="submit" isDisabled={batches === 0 || isProcessing || isLoading || !clientSecret}>
            {isProcessing && 'Processing...'}
            {!isProcessing && showConfirmation && 'Pay Now'}
            {!isProcessing &&
              !showConfirmation &&
              (hasExistingCard ? (
                <div className={Styles.purchaseCreditsCta}>
                  <span>Purchase Credits</span>
                  <div className={Styles.creditsCount}>
                    <SparkleFilled size={14} color="currentColor" />
                    <span>{batches * 100}</span>
                  </div>
                </div>
              ) : (
                'Review Amount'
              ))}
          </Modal.FooterButton>
        </Modal.Footer>
      </form>
    </Modal.Root>
  )
}
